"use client";

import { useEffect, useState, useRef } from "react";
import { useTranslations } from "next-intl";
import { Link, useRouter } from "@/i18n/navigation";
import { Bell } from "lucide-react";
import { api } from "@/lib/api";
import type { Notification, PagedResult } from "@/lib/types";
import { useSignalR } from "@/lib/hooks/use-signalr";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

export function NotificationBell() {
  const t = useTranslations();
  const router = useRouter();
  const [open, setOpen] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const handleNotificationClick = async (notification: Notification) => {
    setOpen(false);
    if (!notification.isRead) {
      try {
        await api.put(`/api/notifications/${notification.id}/read`);
      } catch (err) {
        console.error("Failed to mark notification as read", err);
      }
    }
    router.push("/notifications");
  };

  useEffect(() => {
    let ignore = false;
    async function loadUnreadCount() {
      try {
        const data = await api.get<{ count: number }>("/api/notifications/unread-count");
        if (!ignore) setUnreadCount(data.count);
      } catch (err) {
        console.error("Failed to load unread count", err);
      }
    }
    async function loadLatest() {
      try {
        const data = await api.get<PagedResult<Notification>>("/api/notifications?size=5");
        if (!ignore) setNotifications(data.items);
      } catch (err) {
        console.error("Failed to load recent notifications", err);
      }
    }
    void loadUnreadCount();
    void loadLatest();
    return () => {
      ignore = true;
    };
  }, []);

  // Close on outside click
  useEffect(() => {
    const handleMouseDown = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    if (open) {
      document.addEventListener("mousedown", handleMouseDown);
    }
    return () => document.removeEventListener("mousedown", handleMouseDown);
  }, [open]);

  // SignalR: unread count changed
  useSignalR("UnreadCountChanged", (payload: unknown) => {
    const data = payload as { count: number };
    setUnreadCount(data.count);
  });

  // SignalR: new notification received
  useSignalR("NotificationReceived", (notification: unknown) => {
    const n = notification as Notification;
    setNotifications((prev) => [n, ...prev.slice(0, 4)]);
    setUnreadCount((prev) => prev + 1);
  });

  return (
    <div className="relative" ref={dropdownRef}>
      <Button
        variant="ghost"
        size="icon"
        aria-label={t("dashboard.notifications.title")}
        onClick={() => setOpen((v) => !v)}
        className="relative"
      >
        <Bell className="h-4 w-4" />
        {unreadCount > 0 && (
          <span className="absolute -end-0.5 -top-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-destructive px-1 text-[10px] font-bold text-destructive-foreground">
            {unreadCount > 99 ? "99+" : unreadCount}
          </span>
        )}
      </Button>

      {open && (
        <div className="absolute end-0 top-full z-50 mt-2 w-80 rounded-xl border bg-popover shadow-lg">
          <div className="flex items-center justify-between border-b px-4 py-3">
            <p className="text-sm font-semibold">{t("dashboard.notifications.title")}</p>
            {unreadCount > 0 && (
              <span className="text-xs text-muted-foreground">
                {unreadCount} {t("dashboard.notifications.unreadCount")}
              </span>
            )}
          </div>

          <div className="max-h-80 overflow-y-auto divide-y">
            {notifications.length === 0 ? (
              <div className="px-4 py-8 text-center">
                <Bell className="mx-auto h-8 w-8 text-muted-foreground/40" />
                <p className="mt-2 text-xs text-muted-foreground">{t("dashboard.notifications.noNotifications")}</p>
              </div>
            ) : (
              notifications.map((n) => (
                <button
                  key={n.id}
                  type="button"
                  onClick={() => handleNotificationClick(n)}
                  className={cn(
                    "w-full px-4 py-3 text-start transition-colors hover:bg-muted/50",
                    !n.isRead && "bg-primary/5 hover:bg-primary/10"
                  )}
                >
                  <div className="flex items-start gap-2">
                    {!n.isRead && (
                      <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-primary" />
                    )}
                    <div className="min-w-0 flex-1">
                      <p className={cn("text-xs font-medium truncate", !n.isRead && "ms-0")}>{n.title}</p>
                      <p className="mt-0.5 text-xs text-muted-foreground line-clamp-2">{n.message}</p>
                      <p className="mt-1 text-[10px] text-muted-foreground/60">
                        {new Date(n.createdAt).toLocaleString()}
                      </p>
                    </div>
                  </div>
                </button>
              ))
            )}
          </div>

          <div className="border-t px-4 py-2.5">
            <Link
              href="/notifications"
              className="block text-center text-xs text-primary hover:underline"
              onClick={() => setOpen(false)}
            >
              {t("dashboard.notifications.viewAll")}
            </Link>
          </div>
        </div>
      )}
    </div>
  );
}
